import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TemplateStudioFrame } from "./TemplateStudioFrame";
import { useStudioStoreId } from "./hooks/useStudioStoreId";

export interface StudioRouteSwitchProps {
  /** Path within the Studio origin for the embedded surface. */
  studioPath: string;
  /** Which route param carries the Studio-side resource id, if any. */
  templateIdParam?: "templateId";
  /** i18n key for the iframe's accessible title. */
  titleKey: string;
  /**
   * Height model, forwarded to TemplateStudioFrame. "chrome" for routes
   * inside DashboardLayout (pair with `handle: { studioBleed: true }` on the
   * route so the layout provides the exact-height column); "viewport" for
   * top-level routes with no dashboard chrome.
   */
  fill: "chrome" | "viewport";
  /**
   * The store's business-type code (e.g. "ECOMMERCE"), resolved by the route
   * (see pages/store/TemplatesRoutePage). Forwarded to the Studio so its
   * gallery shows only this store's templates, matching legacy. Omitted =
   * unfiltered.
   */
  businessTypeCode?: string;
  "data-cy": string;
}

/**
 * Renders one template surface as the hosted Studio iframe.
 *
 * The in-dashboard legacy gallery/editor/preview are no longer reachable: the
 * Studio is the only template surface. The widgets still exist under
 * `widgets/template-editor` and `widgets/template-gallery` but nothing imports
 * them, so the bundler never sees them — and with them goes the only live
 * reason the dashboard linked the ~3.3 MB storefront-renderer.
 *
 * Kept as a component rather than inlining TemplateStudioFrame at each route:
 * it owns the host-side navigation contract for studio:open-editor /
 * studio:close, which the frame deliberately does not know about.
 */
export function StudioRouteSwitch({
  studioPath,
  templateIdParam,
  titleKey,
  fill,
  businessTypeCode,
  "data-cy": dataCy,
}: StudioRouteSwitchProps) {
  const { t } = useTranslation();
  const params = useParams<Record<string, string | undefined>>();
  const navigate = useNavigate();
  const storeId = useStudioStoreId();

  return (
    <TemplateStudioFrame
      studioPath={studioPath}
      templateId={templateIdParam ? params[templateIdParam] : undefined}
      title={t(titleKey)}
      fill={fill}
      businessTypeCode={businessTypeCode}
      // Host-side navigation for the Studio's session events. The sandbox has
      // no allow-top-navigation, so route changes that must escape the frame
      // (gallery → full-screen editor, editor close → back to the gallery)
      // arrive as messages and the host navigates the real page.
      lifecycle={{
        onOpenEditor: (templateVersionId) =>
          navigate(`/templates/${encodeURIComponent(templateVersionId)}/editor`),
        // The gallery's "Open full screen": same escape-the-frame problem,
        // different destination — the preview route shows the template at
        // real width without adopting it or starting an editing session.
        onOpenPreview: (templateVersionId) =>
          navigate(
            `/templates/${encodeURIComponent(templateVersionId)}/preview`,
          ),
        // "dashboard" comes from the editor's logo / Dashboard button, whose
        // tooltip has always said "Back to dashboard" — it previously landed
        // on the template list because close had only one destination.
        // "templates" (and any older Studio build, which sends no target at
        // all) keeps going to the gallery, which is what the editor's
        // breadcrumb means by closing.
        onClose: (to) => {
          if (!storeId) return navigate(-1);
          navigate(to === "dashboard" ? `/store/${storeId}` : `/store/${storeId}/templates`);
        },
      }}
      data-cy={dataCy}
    />
  );
}
